import { BindingEngine, computedFrom, observable } from "aurelia-framework";
import { LookupValue, LookupValueRequest } from "../../../../models/lookups/lookup.models";
import { BannerGrouping, RatingsSummaryModel, VesselDetailModel } from "../../../../models/vessels/vessel.models";
import { Reflection } from "../../../../utilities/reflection";
import { EntityItem } from "../entity-item";
import { RatingBaseEntity } from "./base-rating-entity";
import * as moment from "moment";
import { VetClientModel, VettingType } from "../../../../models/vetting/vetting.models";
import { RightShipQiRouterConstants } from "../../../../configuration/rightshipqi-router.constants";
import { Router, RouterEvent } from "aurelia-router";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { NotificationClientService } from "../../../../services/basics/notification-client.service";
import { I18N } from "aurelia-i18n";
import { EventAggregator } from "aurelia-event-aggregator";
import { VetCriteria } from "../vetting/steps/vet-criteria/vet-criteria";
import { VetDocuments } from "../vetting/steps/documents/vet-documents";
import { EntityComments } from "../../common/entity-notes/entity-notes";
import { SignalClientService } from '@rightship-tech/common-ui/services/basic/signal-client.service';
import { EntityStatusChangeModel, SignalFunctions } from "../../../../services/basics/signal-functions";
import { ValidationsClientService } from "../../../../services/validations/validations-client.service";
import { UserClientService } from "../../../../services/authentication/user-client.service";
import { CustomerService as CommonCustomerService } from "@rightship-tech/common-ui/services/basic/customer-client.service";
import { ManageVesselService } from "../../../../services/vessels/manage-vessel.service";
import { EmailHistory } from "./email-history/email-history";
import { LookupClientService } from "../../../../services/lookups/lookup-client.service";
import { ActiveMenuItemsService } from "../../../../services/basics/active-menu-items.service";
import { ClipboardCopyService } from "../../../../utilities/clipboard-copy-service";
import * as Enumerable from "linq";
import { RightShipDialogServiceConstants } from "../../../../constants/basics/rightship-dialog.constants";
import { Claims } from "../../../../constants/authentication/claims";
import { LookupValueQuery } from "../../../../constants/modules/lookup.constants";
import { Assignment } from "../../../components/assignment/assignment";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";

type VetSource = 'vessels' | 'activities';

export abstract class BaseVettingEntity extends RatingBaseEntity<VetClientModel> {

    public shouldRemoveCacheOnDetached: boolean = true;
    public readonly: boolean = true;
    public canViewVetDocuments: boolean;
    public vetStatuses: LookupValue[];
    public cancellationReasons: LookupValue[];
    public canChangeVetStatus: boolean;
    public canAssignVetRequest: boolean;
    public businessUnitId: number;
    public canViewVettingEmailHistory: boolean;
    public reopeningVET: boolean;
    public canProcessSanctionedVet: boolean;
    public canProcessVetUnknownPort: boolean;
    public isSanctioned: boolean;
    public groupedBanners: BannerGrouping[] = [];
    public ratingModel: RatingsSummaryModel;
    public vessel: VesselDetailModel;
    public forceLoadVesselDetails: boolean = false;
    public assignment: Assignment;
    public hasScreening: boolean = true; //TODO
    private showAIExperiment: boolean;

    @observable()
    public entityItem: EntityItem;

    @computedFrom(Reflection.getPropertyPath<BaseVettingEntity>(v => v.router.currentInstruction))
    public get previousInstructionTitle(): string {
        const source: VetSource = this._params.source;

        switch (source) {
            case 'vessels':
                return `${this.i18N.tr('vessel-text')}`;
            case 'activities':
                return `${this.i18N.tr('tasks-text')}`;
            default:
                {
                    const prevInstruction = this.router.currentInstruction.previousInstruction;
                    return prevInstruction
                        ? prevInstruction.config.title
                        : this.i18N.tr('back-text');
                }
        }
    }

    @computedFrom(Reflection.getPropertyPath<BaseVettingEntity>(x => x.item), Reflection.getPropertyPath<BaseVettingEntity>(x => x.item.built))
    public get age() {
        if (this.item) {
            if (this.item.built) {
                return (Math.floor(moment().diff(this.item.built, 'year', true) * 10) / 10).toFixed(1) + " y/o"
            } else {
                return ""
            }
        } else {
            return null;
        }
    }

    @computedFrom("vessel.internationalMaritimeOrganizationNumber")
    get aiExpirimentLink() {

        if (this.vessel && this.vessel.internationalMaritimeOrganizationNumber && this.showAIExperiment) {
            return this.configService.config.urls.redMarbleUrl + '/VesselInfo?imo=' + this.vessel.internationalMaritimeOrganizationNumber
        }

        return null;
    }

    @computedFrom("isSanctionedCountry", "canProcessSanctionedVet", "isUnknownCountry", "canProcessVetUnknownPort")
    get canProcessVet() {
        return (!this.isSanctionedCountry || this.canProcessSanctionedVet) &&
            (!this.isUnknownCountry || this.canProcessVetUnknownPort)
    }

    abstract get isClosed();

    @computedFrom("item", "item.portCountryOfLoadSanctioned", "item.portCountryOfDischargeSanctioned")
    get isSanctionedCountry(): boolean {
        return this.item.portCountryOfLoadSanctioned || this.item.portCountryOfDischargeSanctioned;
    }

    @computedFrom("item", "item.portCountryOfDischargeUnknown", "item.portCountryOfLoadUnknown")
    get isUnknownCountry(): boolean {
        return this.item.portCountryOfDischargeUnknown || this.item.portCountryOfLoadUnknown;
    }

    abstract canDeactivate();

    @computedFrom(Reflection.getPropertyPath<BaseVettingEntity>(v => v.item.vesselId))
    public get vesselUrl() {
        return this.router.generate(RightShipQiRouterConstants.RouteNames.VesselDetails, {
            id: this.item.vesselId,
        });
    }

    abstract getVetStatusLookupParameters(): string;

    constructor(protected router: Router,
        protected bindingEngine: BindingEngine,
        protected dialogClientService: DialogClientService,
        protected i18N: I18N,
        protected userClaimsService: UserClaimsService,
        protected notificationService: NotificationClientService,
        protected eventAggregator: EventAggregator,
        protected vetCriteria: VetCriteria,
        protected vetDocuments: VetDocuments,
        protected entityComments: EntityComments,
        protected signalClientService: SignalClientService,
        protected validationsClientService: ValidationsClientService,
        protected userClientService: UserClientService,
        protected commonCustomerService: CommonCustomerService,
        protected manageVesselService: ManageVesselService,
        protected emailHistory: EmailHistory,
        lookupClientService: LookupClientService,
        private activeMenuItemsService: ActiveMenuItemsService,
        private clipboardCopyService: ClipboardCopyService, private configService: ConfigService) {
        super(router, bindingEngine, i18N, notificationService, eventAggregator, validationsClientService, signalClientService, manageVesselService);
        this.vetCriteria.reloadItemFunc = this.reloadItemFunc.bind(this);
        this.canViewVetDocuments = this.userClaimsService.hasPermission(Claims.CanViewVetDocuments);
        this.canChangeVetStatus = this.userClaimsService.hasPermission(Claims.CanChangeVetStatus);
        this.canAssignVetRequest = this.userClaimsService.hasPermission(Claims.CanAssignVetRequest);
        this.canViewVettingEmailHistory = this.userClaimsService.hasPermission(Claims.CanViewVettingEmailHistory);

        this.canProcessSanctionedVet = this.userClaimsService.hasPermission(Claims.CanProcessSanctionedVet);
        this.canProcessVetUnknownPort = this.userClaimsService.hasPermission(Claims.CanProcessVetUnknownPort);
        this.reopeningVET = this.userClaimsService.hasPermission(Claims.ReopeningVET);
        this.showAIExperiment = this.userClaimsService.hasPermission(Claims.ShowAIExperiment);

        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.VetStatuses;
        request.parameters = this.getVetStatusLookupParameters();
        lookupClientService.getLookup(request).then(x => {
            this.vetStatuses = x.activeValues;
        });

        const requestCancellationReasons = new LookupValueRequest();
        requestCancellationReasons.lookupValueQuery = LookupValueQuery.VetCancellationStatusReasons;
        lookupClientService.getLookup(requestCancellationReasons).then(x => {
            this.cancellationReasons = x.activeValues;
        });

        this.commonCustomerService.getBusinessUnitId().then(x => {
            this.businessUnitId = x;
        });

        eventAggregator.subscribe(RouterEvent.Processing, (x) => {
            if (x && x.instruction && x.instruction.config) {
                const name = x.instruction.config.name;
                this.shouldRemoveCacheOnDetached = name != RightShipQiRouterConstants.RouteNames.PSC && name != RightShipQiRouterConstants.RouteNames.Incident;
            }
        });

    }

    reloadItemFunc: (() => Promise<void>) = async () => {
        this.item = await this.getItem(this.getItemId());
        setTimeout(() => {
            this.refreshSteps();
        });
    }

    abstract getVetCriteriaStep(): number;

    public abstract get statusIsFinal(): boolean;

    public onItemChanged() {

        if (!this.groupedBanners || !this.groupedBanners.length) {

            this.manageVesselService.getVesselBanners(this.item.vesselId).then(x => {

                this.groupedBanners = [];

                Enumerable.from(x).groupBy(x => x.internalName).forEach(groupedBanner => {

                    const bannerGroup = <BannerGrouping>{
                        response: groupedBanner.toArray(),
                        internalName: groupedBanner.toArray()[0].internalName,
                        displayName: groupedBanner.toArray()[0].displayName
                    }

                    this.groupedBanners.push(bannerGroup);
                });
            });
        }
    }

    protected onActivation() {
        this.activeMenuItemsService.setActiveFromMenuItem(this.i18N.tr('tasks-text'));

        if (this._params && this._params.forceLoad) {
            const value = this._params.forceLoad == "true" ? true : false;
            this.onForceLoad(value);
            this.forceLoadVesselDetails = value;
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onForceLoad(value: boolean) { }

    protected getListRoute(): string {
        return RightShipQiRouterConstants.RouteNames.Activities;
    }

    public navigateBack() {
        this.router.navigateBack();
    }

    onAttached() {
        this.signalClientService.subscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.RightShipInspection), this.refreshRightShipInspectionStatus);
        this.refreshRatingModel({ vesselIds: [this.item.vesselId] });
        this.onAttachedOverride();
    }

    abstract onAttachedOverride();
    abstract onDetachedOverride();

    onDetached() {
        this.signalClientService.unsubscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.RightShipInspection), this.refreshRightShipInspectionStatus);
        this.onDetachedOverride();
    }

    private refreshRightShipInspectionStatus = (params: EntityStatusChangeModel) => {
        if (params && this.item.vesselId == params.vesselId) {
            this.loadRatingModel();
        }
    }

    refreshVet = async (param: { vetId: number, entityId: number }) => {
        const id = this.getItemId();
        const updatedVetId = param.entityId ?? param.vetId;
        if (id == updatedVetId) {
            this.item = await this.getItem(id);
            setTimeout(() => {
                this.refreshSteps();
            });
        }
    };

    refreshVetStatus = (param: EntityStatusChangeModel) => {
        if (this.item.vetId == param.entityId) {
            if (param.additionalMeta) {
                this.item.customerDecisionStatusId = param.additionalMeta.customerDecisionStatusId;
                this.item.customerDecisionStatusDate = param.additionalMeta.customerDecisionStatusDate;
                this.item.customerDecisionComment = param.additionalMeta.customerDecisionComment;
                this.item.previousCustomerDecisionStatusId = param.additionalMeta.previousCustomerDecisionStatusId;
                this.item.previousCustomerDecisionStatusDate = param.additionalMeta.previousCustomerDecisionStatusDate;


                this.item.customerDecisionStatusBy = param.additionalMeta.customerDecisionStatusBy;
                this.item.previousCustomerDecisionStatusBy = param.additionalMeta.previousCustomerDecisionStatusBy;

                this.item.starRatingAtVetOutcome = param.additionalMeta.safetyRatingAtVetOutcome;
                this.item.safetyRatingTypeAtVetOutcome = param.additionalMeta.safetyRatingTypeAtVetOutcome;
                this.item.ratingAtVetOutcome = param.additionalMeta.ghgRatingAtVetOutcome;
                this.item.ratingPlusAtVetOutcome = param.additionalMeta.ghgRatingPlusAtVetOutcome;
                this.item.isRatingVerifiedAtVetOutcome = param.additionalMeta.isGhgRatingVerifiedAtVetOutcome;
            }

            const previous = this.item.vetStatusHistory[0] ? this.item.vetStatusHistory[0].statusId : null;
            if (!param.statusId) return;

            if(this.vetStatuses && this.vetStatuses.length > 0)
            {
                const vetStatus = this.vetStatuses.filter(x => x.id == param.statusId)[0];

                if (vetStatus) {
                    const reason = param.cancellationStatusReasonId ? this.cancellationReasons.filter(x => x.id == param.cancellationStatusReasonId).map(x => x.name)[0] : null;
                    this.item.vetStatus = vetStatus.name;
                    this.item.vetStatusId = vetStatus.id;
                    this.item.vetStatusHistory.splice(0, 0, {
                        previousStatusId: previous,
                        createdDate: new Date(),
                        status: vetStatus.name,
                        statusId: vetStatus.id,
                        user: param.userName ? param.userName : "",
                        reasonForCancellation: reason,
                        subtaskTypes: [],
                        comment: param.statusSnippet,
                        fullComment: param.comment
                    });
                }
            }
            

            if (this.assignment) {
                this.assignment.reload().then(x => this.item.assignedUserId = x.assignedTo);
            }

            this.refreshVesselRelatedData();
        }
        //if status chaneg not of this vet, check if vessel is same to refresh open vets on vessel
        else if (this.item.vettingTypeId == VettingType.FullVet && param.vesselId == this.item.vesselId)
        {
            this.refreshVesselRelatedData();
        }
    }

    protected refreshVesselRelatedData() {}

    cancel() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.VetStatusDialog,
            {
                vetId: this.item.vetId,
                isVhr: this.item.isVhr,
                vetStatusId: this.item.vetStatusId,
                isCancellation: true
            }, true, false, false, 'right');
    }

    copy(str: string) {
        this.clipboardCopyService.copy(str);
    }

    private refreshRatingModel = async (param) => {
        if (param && param.vesselIds && param.vesselIds.filter(x => x == this.item.vesselId).length >= 1) {
            this.loadRatingModel();
        }
    }

    async loadRatingModel() {
        try {
            const response = await this.manageVesselService.getRatingsModel(this.item.vesselId);
            if (this.isSanctioned && response) {
                response.rating = "U";
                response.score = 0;
                response.starRating = 0;
                response.isVerified = false;
                response.ratingPlus = null;
                response.sizeScore = 0;
                response.hasRating = false;
            }

            this.ratingModel = response;
            this.onRatingModelLoaded();
        } catch (e) {
            console.log("Failed to load rating model", e);
        }
    }

    abstract onRatingModelLoaded();

    addInspection(inspectionType) {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.AddRightShipInspection, {
            id: 0,
            inspectionTypeId: inspectionType
        });
        //pass the vessel through history instead of url
        this.router.history.setState("vessel", this.vessel);
    }

    entityItemChanged() {
        if (this.entityItem && this.steps && this.steps.length > 0) {
            this.stepsInitialized();
        }
    }
}